// V17.262: the grouped team board table, shared by the page and the wall display.
//
// One component for both surfaces so the numbers on the TV can never disagree
// with the numbers on someone's laptop. `variant` changes only type scale and
// interactivity — never the data or the ordering, which the backend fixes.

import React from 'react';
import type {
  TeamBoard,
  TeamBoardGroup,
  TeamBoardRow,
  TeamBoardTotals,
} from '../../services/arTeamBoard';
import {
  barWidth,
  formatCount,
  formatLak,
  formatPercent,
  progressTone,
} from '../../utils/teamBoardFormat';
import { COLUMN_KEYS, UNASSIGNED_TEAM, useTeamBoardStrings } from '../../utils/teamBoardI18n';

interface Props {
  board: TeamBoard;
  variant: 'page' | 'tv';
  onOfficerClick?: (officerId: string) => void;
}

const TONE_TEXT: Record<string, string> = {
  good: 'text-emerald-600 dark:text-emerald-400',
  warn: 'text-amber-600 dark:text-amber-400',
  bad: 'text-red-600 dark:text-red-400',
  none: 'text-gray-400',
};

const TONE_BAR: Record<string, string> = {
  good: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-red-500',
  none: 'bg-gray-300',
};

function ProgressCell({ value, tv }: { value: number | null; tv: boolean }) {
  const tone = progressTone(value);
  return (
    <td className="px-2 py-1 text-right">
      <div className={`font-bold tabular-nums ${TONE_TEXT[tone]} ${tv ? 'text-xl' : 'text-sm'}`}>
        {formatPercent(value)}
      </div>
      <div className={`mt-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700 ${tv ? 'h-1.5' : 'h-1'}`}>
        <div className={`h-full rounded-full ${TONE_BAR[tone]}`}
             style={{ width: `${barWidth(value)}%` }} />
      </div>
    </td>
  );
}

// V17.289: the "how much work is left" cells. Zero renders green ("done"),
// anything outstanding renders amber so it reads as a call to action; no
// target renders an em dash, never a false-comfort zero.
function RemainingCell({ value, format, tv }: {
  value: number | null;
  format: (v: number | null | undefined) => string;
  tv: boolean;
}) {
  const tone =
    value === null ? TONE_TEXT.none : value === 0 ? TONE_TEXT.good : TONE_TEXT.warn;
  return (
    <td className={`px-2 py-1 text-right tabular-nums font-semibold ${tone} ${tv ? 'text-xl' : 'text-sm'}`}>
      {format(value)}
    </td>
  );
}

function Row({ row, variant, onOfficerClick }: {
  row: TeamBoardRow;
  variant: 'page' | 'tv';
  onOfficerClick?: (id: string) => void;
}) {
  const tv = variant === 'tv';
  const clickable = !tv && Boolean(onOfficerClick);
  const num = `tabular-nums text-right ${tv ? 'text-xl' : 'text-sm'}`;
  const dim = `${num} text-gray-500 dark:text-gray-400`;

  const activate = () => onOfficerClick?.(row.officer_id);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter') {
      activate();
    } else if (event.key === ' ') {
      // Prevent the page from scrolling on Space when the name acts as a button.
      event.preventDefault();
      activate();
    }
  };

  return (
    <tr className="border-b border-gray-100 dark:border-gray-800">
      <td className={dim} data-testid="row-no">{row.no}</td>
      <td className={`px-2 py-1 font-semibold ${tv ? 'text-2xl' : 'text-sm'}`}>
        <span
          data-testid="officer-name"
          className={clickable ? 'cursor-pointer hover:underline' : undefined}
          onClick={clickable ? activate : undefined}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onKeyDown={clickable ? handleKeyDown : undefined}
        >
          {row.officer_name}
        </span>
      </td>
      <td className={dim}>{formatCount(row.call_target)}</td>
      <td className={num}>{formatCount(row.calls_actual)}</td>
      <ProgressCell value={row.call_progress} tv={tv} />
      <RemainingCell value={row.calls_remaining} format={formatCount} tv={tv} />
      <td className={dim} data-testid={`daily-target-${row.officer_id}`}>
        {/* "≈" marks a target derived from remaining monthly pace, not one a
            leader actually set. */}
        {row.daily_target_derived ? '≈ ' : ''}{formatLak(row.daily_target)}
      </td>
      <td className={`${num} font-bold`}>{formatLak(row.collected_today)}</td>
      <ProgressCell value={row.collection_progress} tv={tv} />
      <RemainingCell value={row.collection_remaining} format={formatLak} tv={tv} />
      <td className={dim}>{formatLak(row.month_target)}</td>
      <td className={num}>{formatLak(row.mtd_actual)}</td>
      <ProgressCell value={row.mtd_progress} tv={tv} />
      <RemainingCell value={row.mtd_remaining} format={formatLak} tv={tv} />
    </tr>
  );
}

function TotalRow({ label, total, variant, emphasis }: {
  label: string;
  total: TeamBoardTotals;
  variant: 'page' | 'tv';
  emphasis: boolean;
}) {
  const tv = variant === 'tv';
  const num = `tabular-nums text-right font-bold ${tv ? 'text-xl' : 'text-sm'}`;
  const bg = emphasis
    ? 'bg-blue-50 dark:bg-blue-950 border-t-2 border-blue-300 dark:border-blue-700'
    : 'bg-gray-50 dark:bg-gray-900 border-t border-gray-300 dark:border-gray-700';

  return (
    <tr className={bg}>
      <td />
      <td className={`px-2 py-1 font-bold ${tv ? 'text-2xl' : 'text-sm'}`}>{label}</td>
      <td className={num}>{formatCount(total.call_target)}</td>
      <td className={num}>{formatCount(total.calls_actual)}</td>
      <td className={num}>{formatPercent(total.call_progress)}</td>
      <td className={num}>{formatCount(total.calls_remaining)}</td>
      <td className={num}>{formatLak(total.daily_target)}</td>
      <td className={num}>{formatLak(total.collected_today)}</td>
      <td className={num}>{formatPercent(total.collection_progress)}</td>
      <td className={num}>{formatLak(total.collection_remaining)}</td>
      <td className={num}>{formatLak(total.month_target)}</td>
      <td className={num}>{formatLak(total.mtd_actual)}</td>
      <td className={num}>{formatPercent(total.mtd_progress)}</td>
      <td className={num}>{formatLak(total.mtd_remaining)}</td>
    </tr>
  );
}

function Group({ group, variant, onOfficerClick }: {
  group: TeamBoardGroup;
  variant: 'page' | 'tv';
  onOfficerClick?: (id: string) => void;
}) {
  const t = useTeamBoardStrings();
  return (
    <tbody data-testid={`team-${group.team}`}>
      <tr>
        <td />
        <td colSpan={13}
            className={`px-2 pt-3 pb-1 font-bold ${variant === 'tv' ? 'text-2xl' : 'text-base'}`}>
          {group.team === UNASSIGNED_TEAM ? t.unassigned : group.team}
        </td>
      </tr>
      {group.rows.map((row) => (
        <Row key={row.officer_id} row={row} variant={variant} onOfficerClick={onOfficerClick} />
      ))}
      <TotalRow label={t.teamTotal} total={group.total} variant={variant} emphasis={false} />
    </tbody>
  );
}

export default function TeamBoardTable({ board, variant, onOfficerClick }: Props) {
  const tv = variant === 'tv';
  const t = useTeamBoardStrings();
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b-2 border-gray-300 dark:border-gray-700">
          {COLUMN_KEYS.map((key, index) => (
            <th
              key={key}
              // V17.275: headers used to sit a tier below the body rows they
              // label on both surfaces — text-[10px] over text-sm on the
              // page, text-sm over text-xl on the wall display — so the
              // column names read as fine print.
              //
              // The TV bump is gated on width. The TV page has no scroll
              // (h-screen, ROWS_PER_PAGE = 12) and is tuned for the 1080p
              // wall display, where text-xl headers cost 8px and 69px of
              // slack remain. Narrower than ~1700px the longer labels wrap
              // to three lines and a full 12-row page spills past the
              // footer, so a leader previewing TV mode on a laptop keeps
              // today's text-sm rather than a clipped board.
              className={`px-2 py-2 text-sm font-bold uppercase tracking-wide
                          text-gray-600 dark:text-gray-300
                          ${index < 2 ? 'text-left' : 'text-right'}
                          ${tv ? 'min-[1700px]:text-xl' : ''}`}
            >
              {t.columns[key]}
            </th>
          ))}
        </tr>
      </thead>
      {board.teams.map((group) => (
        <Group key={group.team} group={group} variant={variant} onOfficerClick={onOfficerClick} />
      ))}
      <tbody>
        <TotalRow label={t.grandTotal} total={board.grand_total} variant={variant} emphasis />
      </tbody>
    </table>
  );
}
